/**
 * Public Catalog Page
 * Guest mode - No authentication required
 * For customers to browse available items
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  browsePublicCatalog,
  getPublicCategories,
  CatalogItem,
  CatalogCategory,
  CatalogFilters
} from '../../services/api/catalogService';
import { API_BASE_URL } from '../../utils/constants';

const PublicCatalog = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<CatalogItem[]>([]);
  const [categories, setCategories] = useState<CatalogCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Image Lightbox State
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [selectedImageAlt, setSelectedImageAlt] = useState<string>('');
  
  // Filters
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [inStockOnly, setInStockOnly] = useState(false); // Show all items by default
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 20;

  // Load categories on mount
  useEffect(() => {
    loadCategories();
  }, []);

  // Load items when filters change
  useEffect(() => {
    loadItems();
  }, [selectedCategory, searchQuery, inStockOnly, page]);

  const loadCategories = async () => {
    try {
      const data = await getPublicCategories();
      setCategories(data || []);
    } catch (err: any) {
      console.error('Failed to load categories:', err);
    }
  };

  const loadItems = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const filters: CatalogFilters = {
        category: selectedCategory || undefined,
        search: searchQuery || undefined,
        in_stock_only: inStockOnly,
        page: page,
        page_size: itemsPerPage
      };
      
      const data = await browsePublicCatalog(filters);
      setItems(data.items || []);
      setTotalPages(data.total_pages || 1);
      setTotalItems(data.total || 0);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load catalog');
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(price);
  };

  const getImageUrl = (item: CatalogItem) => {
    // Backend always sends 'url' field regardless of type
    if (item.image?.url) {
      // Handle relative URLs (uploaded images) - backend returns full path with /api/v1/
      if (item.image.url.startsWith('/api/v1/')) {
        // Extract path after /api/v1 (keep the slash after v1)
        const pathAfterApiV1 = item.image.url.substring(7); // Remove '/api/v1' (keep trailing /)
        return `${API_BASE_URL}${pathAfterApiV1}`;
      }
      // External URLs (direct links)
      return item.image.url;
    }
    // Default placeholder - backend expects /api/v1/catalog/placeholder/...
    return `${API_BASE_URL}/catalog/placeholder/${encodeURIComponent(item.category.toLowerCase())}`;
  };

  const handleImageClick = (imageUrl: string, itemName: string) => {
    setSelectedImage(imageUrl);
    setSelectedImageAlt(itemName);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
    setSelectedImageAlt('');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Image Lightbox Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center p-4"
          onClick={closeLightbox}
        >
          <div className="relative max-w-6xl max-h-[90vh] w-full h-full flex items-center justify-center">
            {/* Close Button */}
            <button
              onClick={closeLightbox}
              className="absolute top-4 right-4 bg-white text-gray-800 rounded-full w-10 h-10 flex items-center justify-center hover:bg-gray-200 transition shadow-lg z-10"
              aria-label="Close"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            {/* Zoomed Image - 2x size */}
            <img
              src={selectedImage}
              alt={selectedImageAlt}
              className="max-w-full max-h-full object-contain"
              style={{ 
                transform: 'scale(2)',
                transformOrigin: 'center center',
                maxWidth: '50%',
                maxHeight: '50%'
              }}
              onClick={(e) => e.stopPropagation()}
            />
            
            {/* Image Name */}
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-white bg-opacity-90 px-4 py-2 rounded-lg shadow-lg">
              <p className="text-sm font-medium text-gray-800">{selectedImageAlt}</p>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-blue-600 text-white shadow-lg">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold">📱 Katalog Sparepart Ponsel</h1>
              <p className="text-blue-100 mt-1">Browse dan cek harga sparepart</p>
            </div>
            <button
              onClick={() => navigate('/login')}
              className="bg-white text-blue-600 px-6 py-2 rounded-lg font-medium hover:bg-blue-50 transition"
            >
              Login Kasir
            </button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6">
        {/* Search & Filters */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                🔍 Cari Sparepart
              </label>
              <input
                type="text"
                placeholder="Cari berdasarkan nama atau SKU..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(1);
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* In Stock Filter */}
            <div className="flex items-end">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={inStockOnly}
                  onChange={(e) => {
                    setInStockOnly(e.target.checked);
                    setPage(1);
                  }}
                  className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">
                  Hanya yang ready stock
                </span>
              </label>
            </div>
          </div>

          {/* Category Pills */}
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              📂 Kategori
            </label>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => {
                  setSelectedCategory('');
                  setPage(1);
                }}
                className={`px-4 py-2 rounded-lg font-medium transition ${
                  selectedCategory === ''
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Semua
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.name}
                  onClick={() => {
                    setSelectedCategory(cat.name);
                    setPage(1);
                  }}
                  className={`px-4 py-2 rounded-lg font-medium transition ${
                    selectedCategory === cat.name
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {cat.icon} {cat.name} ({cat.count})
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Stock Info Notice */}
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <div className="text-sm text-blue-800">
              <p className="font-semibold mb-1">ℹ️ Informasi Stok</p>
              <p>Status "Ready" menunjukkan item tersedia di salah satu cabang kami. Untuk memastikan ketersediaan di cabang tertentu, silakan hubungi kami atau kunjungi toko.</p>
            </div>
          </div>
        </div>

        {/* Items Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-gray-600">Memuat katalog...</p>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-lg">
            ❌ {error}
          </div>
        ) : items.length === 0 ? (
          <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-6 py-8 rounded-lg text-center">
            <p className="text-lg font-medium">Tidak ada sparepart ditemukan</p>
            <p className="text-sm mt-1">Coba ubah filter atau kata kunci pencarian</p>
          </div>
        ) : (
          <>
            {/* Items Count Info */}
            <div className="mb-4 text-sm text-gray-600">
              Menampilkan <span className="font-semibold">{((page - 1) * itemsPerPage) + 1}</span> - <span className="font-semibold">{Math.min(page * itemsPerPage, totalItems)}</span> dari <span className="font-semibold">{totalItems}</span> produk
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {items.map((item) => (
                <div key={item.id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition">
                  {/* Image - Clickable for zoom */}
                  <div 
                    className="h-48 bg-gray-100 flex items-center justify-center overflow-hidden cursor-pointer hover:bg-gray-200 transition relative group"
                    onClick={() => handleImageClick(getImageUrl(item), item.name)}
                  >
                    <img
                      src={getImageUrl(item)}
                      alt={item.name}
                      className="max-h-full max-w-full object-contain transition-transform group-hover:scale-105"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = `${API_BASE_URL}/catalog/placeholder/${encodeURIComponent(item.category)}`;
                      }}
                    />
                    {/* Zoom Icon Overlay */}
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition flex items-center justify-center">
                      <div className="bg-white rounded-full p-2 opacity-0 group-hover:opacity-90 transition shadow-lg">
                        <svg className="w-6 h-6 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                        </svg>
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <span className="text-xs font-medium text-gray-500">{item.category}</span>
                      {item.in_stock ? (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded" title="Tersedia di salah satu cabang">
                          ✓ Ready
                        </span>
                      ) : (
                        <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded" title="Stok habis di semua cabang">
                          ✗ Habis
                        </span>
                      )}
                    </div>

                    <h3 className="font-semibold text-gray-800 mb-1 line-clamp-2">
                      {item.name}
                    </h3>
                    
                    <p className="text-xs text-gray-500 mb-2">SKU: {item.sku}</p>
                    
                    {item.description && (
                      <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                        {item.description}
                      </p>
                    )}

                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-2xl font-bold text-blue-600">
                          {formatPrice(item.price)}
                        </p>
                      </div>
                      {item.has_warranty && (
                        <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">
                          🛡️ Garansi
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-8 flex flex-col items-center gap-4">
                {/* Page Info */}
                <div className="text-sm text-gray-600">
                  Halaman <span className="font-semibold text-blue-600">{page}</span> dari <span className="font-semibold">{totalPages}</span>
                </div>
                
                {/* Pagination Controls */}
                <div className="flex items-center gap-2">
                  {/* First Page */}
                  <button
                    onClick={() => setPage(1)}
                    disabled={page === 1}
                    className="px-3 py-2 bg-white border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition"
                    title="Halaman Pertama"
                  >
                    «
                  </button>
                  
                  {/* Previous Page */}
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-4 py-2 bg-white border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition"
                  >
                    ← Sebelumnya
                  </button>
                  
                  {/* Page Numbers */}
                  <div className="flex gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (page <= 3) {
                        pageNum = i + 1;
                      } else if (page >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = page - 2 + i;
                      }
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setPage(pageNum)}
                          className={`px-4 py-2 rounded-lg font-medium transition ${
                            page === pageNum
                              ? 'bg-blue-600 text-white'
                              : 'bg-white border border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  
                  {/* Next Page */}
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="px-4 py-2 bg-white border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition"
                  >
                    Selanjutnya →
                  </button>
                  
                  {/* Last Page */}
                  <button
                    onClick={() => setPage(totalPages)}
                    disabled={page === totalPages}
                    className="px-3 py-2 bg-white border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition"
                    title="Halaman Terakhir"
                  >
                    »
                  </button>
                </div>
                
                {/* Direct Page Jump */}
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-600">Langsung ke halaman:</label>
                  <input
                    type="number"
                    min="1"
                    max={totalPages}
                    value={page}
                    onChange={(e) => {
                      const val = parseInt(e.target.value);
                      if (val >= 1 && val <= totalPages) {
                        setPage(val);
                      }
                    }}
                    className="w-20 px-3 py-1 border border-gray-300 rounded-lg text-center focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            )}
          </>
        )}

        {/* Footer Info */}
        <div className="mt-12 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="font-semibold text-blue-900 mb-2">ℹ️ Informasi</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Harga dapat berubah sewaktu-waktu</li>
            <li>• Stok ditampilkan secara real-time</li>
            <li>• Untuk pemesanan, silakan hubungi kasir kami</li>
            <li>• Garansi berlaku sesuai ketentuan yang tertera</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default PublicCatalog;
